import { BusinessOutlined, ForumOutlined } from '@mui/icons-material';
import { Avatar, AvatarProps } from '@mui/material';
import { Emblem, EmblemEntityType } from 'middleware-types';
import { EmblemDropdown } from './emblem-dropdown/emblem-dropdown';
import { useEmblem } from './hooks';

export interface EmblemAvatarBaseProps extends AvatarProps {
	size?: number;
	noDropdown?: boolean;
}

interface EmblemAvatarProps extends EmblemAvatarBaseProps {
	entityId: string;
	entityType: EmblemEntityType;
}

const _EmblemAvatar = ({
	entityId,
	entityType,
	size = 40,
	noDropdown = false,
	sx,
	...avatarProps
}: EmblemAvatarProps) => {
	const { emblem, src, onError } = useEmblem(entityId, entityType);

	let variant: AvatarProps['variant'] = undefined;
	let children: AvatarProps['children'] = undefined;
	switch (entityType) {
		case EmblemEntityType.User:
			variant = 'circular';
			children = emblem?.extraData?.initials;
			break;
		case EmblemEntityType.Organization:
			variant = 'rounded';
			children = <BusinessOutlined sx={{ fontSize: '1.25em' }} />;
			break;
		case EmblemEntityType.Conversation:
			variant = 'circular';
			children = <ForumOutlined sx={{ fontSize: '1.25em' }} />;
			break;
	}

	const props: AvatarProps = {
		src,
		variant,
		onError,
		children,
		sx: { height: size, width: size, fontSize: size / 2, ...sx },
		...avatarProps,
	};

	if (emblem === undefined || noDropdown) return <Avatar {...props} />;
	return (
		<EmblemDropdown emblem={emblem}>
			<Avatar {...props} />
		</EmblemDropdown>
	);
};

/**
 * wrappers for the emblem avatar component
 */
export const EmblemAvatar = ({
	emblem,
	...rest
}: EmblemAvatarBaseProps & { emblem: Emblem | undefined }) => {
	if (emblem === undefined) return null;
	return <_EmblemAvatar entityId={emblem.id} entityType={emblem.entityType} {...rest} />;
};

export const UserEmblemAvatar = ({ id, ...rest }: EmblemAvatarBaseProps & { id: string }) => {
	return <_EmblemAvatar entityId={id} entityType={EmblemEntityType.User} {...rest} />;
};

export const OrgEmblemAvatar = ({ id, ...rest }: EmblemAvatarBaseProps & { id: string }) => {
	return <_EmblemAvatar entityId={id} entityType={EmblemEntityType.Organization} {...rest} />;
};
